import React, { useState, useEffect } from "react";
import { Doughnut } from "react-chartjs-2";
import { Bar } from "react-chartjs-2";
import "../../Styles/Faculty.css";
import teacher from "../../assets/teacher.png";
import books from "../../assets/books.png";
import expired from "../../assets/expired.png";
import axios from "axios";
import Loader from "../../Components/Loader";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function Home() {
  const [currentTime, setCurrentTime] = useState(new Date());
  const formattedTime = currentTime.toLocaleString();
  const [facultyData, setFacultyData] = useState({
    subjectCount: 0,
    classCount: 0,
    name: ""  // Added default value for name
  });
  const [attendanceData, setAttendanceData] = useState({
    present: 0,
    absent: 0,
    total: 0,
  });
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [loading, setLoading] = useState(true);
  const [monthChangeLoading, setMonthChangeLoading] = useState(false);
  const [announcements, setAnnouncements] = useState([]); // Initialize as empty array

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchAnnouncements = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("authToken");
        const response = await axios.get(
          "https://api.uniums.com/api/announcement/view-announcements-for-user",
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        // Make sure we're setting a valid array even if response structure is unexpected
        if (response.data && response.data.data) {
          setAnnouncements(response.data.data);
          toast.success("Announcements fetched successfully!");
        } else {
          setAnnouncements([]); // Set to empty array if data is invalid
          toast.warning("No announcements found");
        }
      } catch (error) {
        console.error("Error fetching announcements:", error);
        toast.error("Failed to fetch announcements.");
        setAnnouncements([]); // Ensure announcements is at least an empty array on error
      } finally {
        setLoading(false);
      }
    };

    fetchAnnouncements();
  }, []);

  const fetchAttendanceData = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        console.error("No authentication token found");
        return;
      }

      setMonthChangeLoading(true);

      const attendanceResponse = await axios.get(`https://api.uniums.com/api/faculty/faculty-attendance?month=${selectedMonth}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (attendanceResponse.data && attendanceResponse.data.data) {
        setAttendanceData(attendanceResponse.data.data);
      }
    } catch (error) {
      console.error("Error fetching attendance data:", error.response ? error.response.data : error.message);
      toast.error("Failed to fetch attendance data.");
    } finally {
      setMonthChangeLoading(false);
    }
  };

  useEffect(() => {
    const fetchFacultyData = async () => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) {
          console.error("No authentication token found");
          return;
        }

        const facultyResponse = await axios.get("https://api.uniums.com/api/faculty/faculty-dashboard", {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (facultyResponse.data && facultyResponse.data.data) {
          setFacultyData(facultyResponse.data.data);
        }

        await fetchAttendanceData();

        const pendingLeaveResponse = await axios.get("https://api.uniums.com/api/faculty/faculty-pending-leave", {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (pendingLeaveResponse.data && pendingLeaveResponse.data.notifications) {
          setPendingApprovals(pendingLeaveResponse.data.notifications.length);
        }
      } catch (error) {
        console.error("Error fetching data:", error.response ? error.response.data : error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchFacultyData();
  }, []);

  useEffect(() => {
    // When selectedMonth changes, fetch the new attendance data
    if (!loading) {
      fetchAttendanceData();
    }
  }, [selectedMonth]);

  if (loading) {
    return <Loader />;
  }

  // Data for charts
  const pieData = {
    labels: ["Present", "Absent"],
    datasets: [
      {
        data: [attendanceData.present, attendanceData.absent],
        backgroundColor: ["#4CAF50", "#FF9800"],
        borderColor: ["#4CAF50", "#FF9800"],
        borderWidth: 1,
      },
    ],
  };

  const pieOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
    },
  };

  const barData = {
    labels: ["Absent", "Present"],
    datasets: [
      {
        label: "Absent",
        data: [attendanceData.absent, 0],
        backgroundColor: "#FF5252",
        borderColor: "#FF5252",
        borderWidth: 1,
      },
      {
        label: "Present",
        data: [0, attendanceData.present],
        backgroundColor: "#4CAF50",
        borderColor: "#4CAF50",
        borderWidth: 1,
      }
    ],
  };

  const barOptions = {
    responsive: true,
    scales: {
      y: {
        beginAtZero: true,
      },
    },
    plugins: {
      legend: {
        position: "top",
      },
    },
  };

  const monthOptions = Array.from({ length: 12 }, (_, index) => {
    const month = new Date(0, index).toLocaleString('default', { month: 'long' });
    return { value: index, label: month };
  });

  const handleMonthChange = (event) => {
    setSelectedMonth(Number(event.target.value));
  };

  return (
    <>
      <div className="faculty-dashboard">
        <div className="marquee-container">
          <div className="marquee">
            Welcome to UNIUMS {facultyData.name || ""} | Current Time: {formattedTime}
          </div>
        </div>
        <div className="card-container1">
          <div className="card_f">
            <img src={teacher} className="faculty_img_card" alt="teacher" />
            <div className="card_content">
              <h3>Total Classes</h3>
              <p>{facultyData.classCount}</p>
            </div>
          </div>
          <div className="card_f">
            <img src={books} className="faculty_img_card" alt="books" />
            <div className="card_content">
              <h3>Subjects Assigned</h3>
              <p>{facultyData.subjectCount}</p>
            </div>
          </div>
          <div className="card_f">
            <img src={expired} className="faculty_img_card" alt="expired" />
            <div className="card_content">
              <h3>Pending Approvals</h3>
              <p>{pendingApprovals}</p>
            </div>
          </div>
        </div>
        <div className="month-filter">
          <label>Select Month: </label>
          <select onChange={handleMonthChange} value={selectedMonth}>
            {monthOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {monthChangeLoading ? (
          <Loader />
        ) : (
          <div className="charts_container">
            <div className="chart doughnut-chart1">
              <h3>Attendance Overview</h3>
              <Doughnut data={pieData} options={pieOptions} />
            </div>
            <div className="chart bar-chart1">
              <h3>Attendance Breakdown</h3>
              <Bar data={barData} options={barOptions} />
            </div>
          </div>
        )}

        <div className="announcement-section">
          <h3>Announcements</h3>
          <ul>
            {announcements && announcements.length > 0 ? (
              announcements.map((announcement, index) => (
                <li key={index} className="announcement-card-list">
                  <h4>{announcement.title} </h4>
                  <span>
                    <strong>Date:</strong>{" "}
                    {new Date(announcement.date).toLocaleDateString()} |
                    <strong>Time:</strong> {announcement.time}
                  </span>
                  <p className="announcement-p">{announcement.description}</p>

                  <p>
                    <strong>Audience:</strong> {announcement.audience}
                  </p>
                  {announcement.location && (
                    <p>
                      <strong>Location:</strong> {announcement.location}
                    </p>
                  )}
                  {announcement.attachment && (
                    <p>
                      <a
                        href={announcement.attachment}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View Attachment
                      </a>
                    </p>
                  )}
                </li>
              ))
            ) : (
              <li>No announcements available at the moment.</li>
            )}
          </ul>
        </div>
      </div>
      <ToastContainer />
    </>
  );
}

export default Home;